import { Link } from "react-router-dom";

export default function SEOContent() {
  return (
    <section className="py-16 md:py-24 px-6 md:px-10 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-16">
          <div className="lg:w-[65%]">
            <p className="inline-block px-3 py-1 rounded-full bg-accent-100/80 text-accent-800 text-xs font-medium mb-6">
              Shopping Intelligence
            </p>

            <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
              Structured
              <br />
              Price
              <br />
              Intelligence
            </h2>

            <div className="mt-10 space-y-6">
              <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900">
                Why Online Shopping Feels Expensive and Confusing
              </h3>
              <p className="text-foreground-600 leading-relaxed">
                Most online shoppers face the same frustrating problems: prices differ dramatically across platforms with zero visibility, there is no structured way to compare products side by side, tracking price history is nearly impossible without dedicated tools, manual checking across multiple websites wastes hours every week, and without a centralized data approach, shoppers end up overpaying without even realizing it.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
                {[
                  "Prices differ across platforms with no visibility",
                  "No structured comparison system available",
                  "Hard to track product price history over time",
                  "Manual checking wastes valuable time",
                  "No centralized data model for smart decisions",
                  "Users overpay without realizing the alternatives",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <i className="ri-close-circle-line text-accent-600 mt-0.5 flex-shrink-0"></i>
                    <span className="text-sm text-foreground-600">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-12">
              <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900">
                What is the GTBuy Spreadsheet System?
              </h3>
              <p className="mt-4 text-foreground-600 leading-relaxed">
                The <strong>GTBuy Spreadsheet</strong> system is a structured comparison framework that allows users to compare product prices across multiple platforms, track historical price changes over weeks and months, evaluate Kakobuy Spreadsheet and Superbuy Spreadsheet systems side by side, and build data-driven purchasing decisions based on real numbers rather than impulse. It is not just another tool — it is a complete shopping intelligence system designed for smart buyers who want to maximize value on every purchase.
              </p>
            </div>

            <div className="mt-14">
              <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-6">
                Core Comparison: GTBuy vs Kakobuy vs Superbuy
              </h3>
              <div className="overflow-x-auto rounded-xl border border-background-200/70">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-primary-700 text-background-50">
                      <th className="text-left px-5 py-3.5 font-semibold whitespace-nowrap">Feature</th>
                      <th className="text-left px-5 py-3.5 font-semibold whitespace-nowrap">
                        <strong>GTBuy Spreadsheet</strong>
                      </th>
                      <th className="text-left px-5 py-3.5 font-semibold whitespace-nowrap">Kakobuy Spreadsheet</th>
                      <th className="text-left px-5 py-3.5 font-semibold whitespace-nowrap">Superbuy Spreadsheet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Price Tracking", "Advanced", "Medium", "Medium"],
                      ["Data Structure", "Highly Organized", "Basic", "Medium"],
                      ["Automation Level", "High", "Low", "Medium"],
                      ["Best Use Case", "Smart Buyers", "Beginners", "Bulk Buyers"],
                      ["Decision Accuracy", "Very High", "Medium", "High"],
                      ["Learning Curve", "Moderate", "Low", "Moderate"],
                    ].map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? "bg-background-50" : "bg-background-100"}>
                        <td className="px-5 py-3 font-medium text-foreground-800 whitespace-nowrap">{row[0]}</td>
                        <td className="px-5 py-3 text-primary-700 font-semibold whitespace-nowrap">{row[1]}</td>
                        <td className="px-5 py-3 text-foreground-600 whitespace-nowrap">{row[2]}</td>
                        <td className="px-5 py-3 text-foreground-600 whitespace-nowrap">{row[3]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-12">
              <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-6">
                Key Benefits of Spreadsheet-Based Shopping
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: "ri-money-dollar-circle-line", title: "Reduce Unnecessary Spending", desc: "Identify the best prices across all platforms instantly" },
                  { icon: "ri-scales-3-line", title: "Compare Multiple Platforms", desc: "Side-by-side evaluation of GTBuy, Kakobuy, and Superbuy" },
                  { icon: "ri-focus-3-line", title: "Improve Buying Accuracy", desc: "Data-driven decisions replace impulsive purchases" },
                  { icon: "ri-database-2-line", title: "Centralized Intelligence", desc: "One spreadsheet for all your shopping research" },
                  { icon: "ri-line-chart-line", title: "Scalable System", desc: "Grows with your shopping needs over time" },
                  { icon: "ri-time-line", title: "Save Hours Weekly", desc: "Automated comparison beats manual browsing" },
                ].map((benefit, i) => (
                  <div key={i} className="flex items-start gap-4 p-5 rounded-xl bg-background-100 border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                      <i className={`${benefit.icon} text-accent-700 text-lg`}></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground-900 text-sm">{benefit.title}</h4>
                      <p className="text-xs text-foreground-500 mt-1 leading-relaxed">{benefit.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-14 flex flex-col sm:flex-row gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center px-7 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm cursor-pointer whitespace-nowrap hover:bg-primary-600 transition-colors"
              >
                GTBuy Sheet
                <i className="ri-arrow-right-up-line ml-2"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center px-7 py-3 rounded-full bg-secondary-500 text-background-50 font-semibold text-sm cursor-pointer whitespace-nowrap hover:bg-secondary-600 transition-colors"
              >
                Buy It Now
                <i className="ri-arrow-right-up-line ml-2"></i>
              </a>
              <Link
                to="/article/what-is-gtbuy-spreadsheet"
                className="inline-flex items-center justify-center px-7 py-3 rounded-full border-2 border-primary-500 text-primary-700 font-semibold text-sm cursor-pointer whitespace-nowrap hover:bg-primary-500 hover:text-background-50 transition-colors"
              >
                Read Full Guide
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>

            <div className="mt-12 p-6 rounded-xl bg-background-100 border border-background-200/70">
              <h4 className="font-heading text-lg font-semibold text-foreground-900 mb-3">
                Important Notice
              </h4>
              <ul className="space-y-2 text-xs text-foreground-500">
                <li className="flex items-start gap-2">
                  <i className="ri-information-line mt-0.5 flex-shrink-0"></i>
                  Data is informational only and should be verified independently
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-information-line mt-0.5 flex-shrink-0"></i>
                  Prices may change without notice across all platforms
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-information-line mt-0.5 flex-shrink-0"></i>
                  Always verify product details before making a purchase
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-information-line mt-0.5 flex-shrink-0"></i>
                  No financial guarantee is provided — shop responsibly
                </li>
              </ul>
            </div>
          </div>

          <div className="lg:w-[35%]">
            <div className="sticky top-24 space-y-8">
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20product%20photography%20of%20a%20modern%20lifestyle%20arrangement%20with%20a%20notebook%20and%20shopping%20items%20on%20a%20cream%20marble%20surface%2C%20soft%20natural%20daylight%2C%20warm%20neutral%20aesthetic%2C%20editorial%20flat%20lay%20style%2C%20textured%20linen%2C%20sage%20green%20accents%2C%20premium%20commercial%20photography%2C%20clean%20composition&width=500&height=700&seq=seo-sidebar-gtbuy&orientation=portrait"
                  alt="Shopping intelligence sidebar visual"
                  className="w-full rounded-2xl"
                />
              </div>
              <div className="p-6 rounded-2xl bg-accent-100/50 border border-accent-200/50">
                <h4 className="font-heading text-lg font-semibold text-foreground-900 mb-3">
                  Want to Learn More?
                </h4>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  Read our in-depth guide on how the GTBuy Spreadsheet system works and how it compares to Kakobuy and Superbuy platforms.
                </p>
                <Link
                  to="/article/what-is-gtbuy-spreadsheet"
                  className="inline-flex items-center text-sm font-semibold text-primary-700 hover:text-primary-800 cursor-pointer whitespace-nowrap"
                >
                  Read the Complete Guide
                  <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}